import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CaretLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { submitInquiry } from "@/lib/enrollment";
import { listTeacherClassrooms } from "@/lib/dailyReports";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import { SOURCE_LABELS, SOURCES } from "@/lib/enrollmentTypes";

export default function ParentEnrollmentForm() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [classrooms, setClassrooms] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    parentFirstName: "",
    parentLastName: "",
    parentEmail: profile?.email || "",
    parentPhone: "",
    childFirstName: "",
    childLastName: "",
    childDob: "",
    preferredClassroomId: "",
    preferredAgeGroup: "",
    preferredStartDate: "",
    parentNotes: "",
    source: "website",
  });

  // Preload classroom list for the optional preference dropdown.
  useEffect(() => {
    (async () => {
      try {
        if (isSupabaseConfigured) {
          const { data } = await supabase
            .from("classrooms")
            .select("id, name, age_group")
            .order("name");
          setClassrooms(data || []);
        } else {
          // Demo: reuse teacher's classroom list helper — returns demo fixtures.
          const rows = await listTeacherClassrooms(profile?.id || "demo");
          setClassrooms(rows);
        }
      } catch {
        // Non-critical; form still submittable.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fill parent name from profile.full_name when available.
  useEffect(() => {
    if (!profile?.full_name) return;
    const parts = profile.full_name.trim().split(/\s+/);
    setForm((f) => ({
      ...f,
      parentFirstName: f.parentFirstName || parts[0] || "",
      parentLastName: f.parentLastName || parts.slice(1).join(" ") || "",
    }));
  }, [profile?.full_name]);

  const emailErr =
    form.parentEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.parentEmail)
      ? "Enter a valid email."
      : null;

  const canSubmit = useMemo(
    () => form.parentFirstName.trim() && form.parentEmail.trim() && !emailErr,
    [form, emailErr],
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const row = await submitInquiry({
        parentFirstName: form.parentFirstName.trim(),
        parentLastName: form.parentLastName.trim() || null,
        parentEmail: form.parentEmail.trim(),
        parentPhone: form.parentPhone.trim() || null,
        childFirstName: form.childFirstName.trim() || null,
        childLastName: form.childLastName.trim() || null,
        childDob: form.childDob || null,
        preferredClassroomId: form.preferredClassroomId || null,
        preferredAgeGroup: form.preferredAgeGroup.trim() || null,
        preferredStartDate: form.preferredStartDate || null,
        parentNotes: form.parentNotes.trim() || null,
        source: form.source,
        submittedBy: profile?.id || null,
      });
      toast.success("Inquiry submitted — we'll be in touch soon.");
      navigate(`/parent/enrollment/${row.id}`);
    } catch (err) {
      toast.error(err.message || "Could not submit inquiry");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="parent-enrollment-form" className="space-y-5">
      <Link
        to="/parent/enrollment"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900"
        data-testid="enr-form-back"
      >
        <CaretLeft size={16} weight="bold" />
        Back
      </Link>
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          New inquiry
        </p>
        <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900">
          Tell us about your family
        </h1>
        <p className="mt-2 text-sm text-neutral-600">
          We'll review your inquiry and get back to you within a few business
          days. Only the information you provide here is shared with our team.
        </p>
      </header>

      <form onSubmit={onSubmit} className="space-y-5">
        <Section title="Your details">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="First name">
              <input
                data-testid="enr-parent-first"
                type="text"
                required
                value={form.parentFirstName}
                onChange={(e) => setForm((f) => ({ ...f, parentFirstName: e.target.value }))}
                className={inputClass}
              />
            </Field>
            <Field label="Last name">
              <input
                data-testid="enr-parent-last"
                type="text"
                value={form.parentLastName}
                onChange={(e) => setForm((f) => ({ ...f, parentLastName: e.target.value }))}
                className={inputClass}
              />
            </Field>
            <Field label="Email" error={emailErr}>
              <input
                data-testid="enr-parent-email"
                type="email"
                required
                value={form.parentEmail}
                onChange={(e) => setForm((f) => ({ ...f, parentEmail: e.target.value }))}
                className={inputClass}
              />
            </Field>
            <Field label="Phone (optional)">
              <input
                data-testid="enr-parent-phone"
                type="tel"
                value={form.parentPhone}
                onChange={(e) => setForm((f) => ({ ...f, parentPhone: e.target.value }))}
                className={inputClass}
              />
            </Field>
          </div>
        </Section>

        <Section title="About your child">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Child's first name">
              <input
                data-testid="enr-child-first"
                type="text"
                value={form.childFirstName}
                onChange={(e) => setForm((f) => ({ ...f, childFirstName: e.target.value }))}
                className={inputClass}
              />
            </Field>
            <Field label="Child's last name">
              <input
                data-testid="enr-child-last"
                type="text"
                value={form.childLastName}
                onChange={(e) => setForm((f) => ({ ...f, childLastName: e.target.value }))}
                className={inputClass}
              />
            </Field>
            <Field label="Date of birth">
              <input
                data-testid="enr-child-dob"
                type="date"
                value={form.childDob}
                onChange={(e) => setForm((f) => ({ ...f, childDob: e.target.value }))}
                className={inputClass}
              />
            </Field>
            <Field label="Preferred start date">
              <input
                data-testid="enr-start-date"
                type="date"
                value={form.preferredStartDate}
                onChange={(e) => setForm((f) => ({ ...f, preferredStartDate: e.target.value }))}
                className={inputClass}
              />
            </Field>
          </div>
        </Section>

        <Section title="Program preferences">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Preferred classroom (optional)">
              <select
                data-testid="enr-classroom"
                value={form.preferredClassroomId}
                onChange={(e) =>
                  setForm((f) => ({ ...f, preferredClassroomId: e.target.value }))
                }
                className={inputClass}
              >
                <option value="">No preference</option>
                {classrooms.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {c.age_group ? ` · ${c.age_group}` : ""}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="How did you hear about us?">
              <select
                data-testid="enr-source"
                value={form.source}
                onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}
                className={inputClass}
              >
                {SOURCES.map((s) => (
                  <option key={s} value={s}>{SOURCE_LABELS[s]}</option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Anything else you'd like us to know?">
            <textarea
              data-testid="enr-parent-notes"
              rows={4}
              value={form.parentNotes}
              onChange={(e) => setForm((f) => ({ ...f, parentNotes: e.target.value }))}
              placeholder="Tell us about your child's personality, routines, or any specific program features you're interested in."
              className={inputClass}
            />
          </Field>
        </Section>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Link
            to="/parent/enrollment"
            data-testid="enr-cancel"
            className="rounded-full px-5 py-2.5 text-sm font-medium text-neutral-600 hover:bg-neutral-100"
          >
            Cancel
          </Link>
          <button
            type="submit"
            data-testid="enr-submit"
            disabled={!canSubmit || submitting}
            className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? "Submitting…" : "Submit inquiry"}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputClass =
  "w-full rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";

function Section({ title, children }) {
  return (
    <section className="rounded-2xl border border-neutral-200 bg-white p-5 space-y-3">
      <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
        {title}
      </p>
      {children}
    </section>
  );
}

function Field({ label, children, error }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
        {label}
      </span>
      {children}
      {error && <p className="mt-1 text-xs text-[#C6851E]">{error}</p>}
    </label>
  );
}
